import React, { useState, useEffect } from 'react';
import { tracksAPI } from '../../services/api';
import './Admin.css';

const TracksManagement = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, blocked
  const [sortBy, setSortBy] = useState('created_at'); // created_at, title
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc

  useEffect(() => {
    loadTracks();
  }, []);

  const loadTracks = async () => {
    try {
      setLoading(true);
      const response = await tracksAPI.getAll();
      setTracks(response.data.tracks);
    } catch (error) {
      setError('Failed to load tracks');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      loadTracks();
      return;
    }

    try {
      const response = await tracksAPI.search(searchTerm);
      setTracks(response.data.tracks);
    } catch (error) {
      setError('Search failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze track wilt verwijderen?')) {
      return;
    }

    try {
      await tracksAPI.delete(id);
      setSuccess('Track verwijderd');
      setTimeout(() => setSuccess(''), 3000);
      loadTracks();
    } catch (error) {
      setError('Kon track niet verwijderen');
    }
  };

  const handleBlock = async (id) => {
    try {
      await tracksAPI.block(id);
      setSuccess('Track geblokkeerd - wordt niet meer afgespeeld');
      setTimeout(() => setSuccess(''), 3000);
      loadTracks();
    } catch (error) {
      setError('Kon track niet blokkeren');
    }
  };

  const handleUnblock = async (id) => {
    try {
      await tracksAPI.unblock(id);
      setSuccess('Track gedeblokkeerd - kan weer worden afgespeeld');
      setTimeout(() => setSuccess(''), 3000);
      loadTracks();
    } catch (error) {
      setError('Kon track niet deblokkeren');
    }
  };

  const getFilteredTracks = () => {
    let filtered = [...tracks];

    // Filter op status
    if (filterStatus === 'active') {
      filtered = filtered.filter(t => !t.blocked);
    } else if (filterStatus === 'blocked') {
      filtered = filtered.filter(t => t.blocked);
    }

    // Sorteren
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'title') {
        aVal = aVal?.toLowerCase() || '';
        bVal = bVal?.toLowerCase() || '';
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="tracks-management">
      <header className="page-header">
        <h1>Tracks Beheer</h1>
        <p>Beheer je muziek bibliotheek ({getFilteredTracks().length} van {tracks.length} tracks)</p>
      </header>

      {error && <div className="message error">{error}</div>}
      {success && <div className="message success">{success}</div>}

      <div className="filters-bar">
        <div className="search-box">
          <form onSubmit={handleSearch} className="search-form">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Zoek op titel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" className="btn-primary">Zoek</button>
            {searchTerm && (
              <button 
                type="button" 
                onClick={() => { setSearchTerm(''); loadTracks(); }}
                className="btn-clear"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </form>
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <label><i className="fas fa-filter"></i> Status:</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">Alle tracks</option>
              <option value="active">Actief</option>
              <option value="blocked">Geblokkeerd</option>
            </select>
          </div>

          <div className="filter-group">
            <label><i className="fas fa-sort"></i> Sorteer op:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="created_at">Datum toegevoegd</option>
              <option value="title">Titel</option>

            </select>
          </div>

          <div className="filter-group">
            <label><i className="fas fa-arrows-alt-v"></i> Volgorde:</label>
            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
              <option value="desc">Aflopend</option>
              <option value="asc">Oplopend</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading"><i className="fas fa-spinner fa-spin"></i> Tracks laden...</div>
      ) : tracks.length === 0 ? (
        <div className="no-data">
          <i className="fas fa-music"></i>
          <p>Nog geen tracks geüpload.</p>
          <p>Upload je eerste track om te beginnen!</p>
        </div>
      ) : getFilteredTracks().length === 0 ? (
        <div className="no-data">
          <i className="fas fa-search"></i>
          <p>Geen tracks gevonden met de huidige filters.</p>
        </div>
      ) : (
        <div className="tracks-table-container">
          <table className="tracks-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Titel</th>
                <th>Duur</th>
                <th>Toegevoegd</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredTracks().map((track) => (
                <tr key={track.id} className={track.blocked ? 'blocked-row' : ''}>
                  <td>
                    {track.blocked ? (
                      <span className="status-badge blocked">
                        <i className="fas fa-ban"></i> Geblokkeerd
                      </span>
                    ) : (
                      <span className="status-badge active">
                        <i className="fas fa-check-circle"></i> Actief
                      </span>
                    )}
                  </td>
                  <td className="title-cell">
                    <strong>{track.title}</strong>
                    {track.artist && <span className="artist-name">{track.artist}</span>}
                  </td>
                  <td>{formatDuration(track.duration)}</td>
                  <td>{new Date(track.created_at).toLocaleDateString('nl-NL')}</td>
                  <td>
                    <div className="action-buttons">
                      {track.blocked ? (
                        <button 
                          onClick={() => handleUnblock(track.id)}
                          className="btn-unblock"
                          title="Deblokkeer track"
                        >
                          <i className="fas fa-check"></i> Deblokkeer
                        </button>
                      ) : (
                        <button 
                          onClick={() => handleBlock(track.id)}
                          className="btn-block"
                          title="Blokkeer track"
                        >
                          <i className="fas fa-ban"></i> Blokkeer
                        </button>
                      )}
                      <button 
                        onClick={() => handleDelete(track.id)}
                        className="btn-delete"
                        title="Verwijder track"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TracksManagement;
